import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { LoginManager } from '../../core/login-manager';
import { UtentiManager } from '../../lib/utenti-manager';
import { nomeUtente, refId, refLabel } from '../../lib/format';
import { CONDOMINO, etichettaLivello, isAdminGlobale, livelloDi, richiedeApprovazione } from '../../lib/ruoli';
import { useEntityList, SectionHead, statoLista, OpenLink, ConfirmModal, messaggioErrore } from './common';
import Select from '../../components/Select';

/**
 * Anagrafica degli utenti del condominio.
 *
 * Qui si guarda e si cerca; per creare o modificare si va sulla pagina
 * dell'utente, che ha spazio per i dieci campi che servono davvero.
 *
 * Un utente puo' prenotare solo con `abilitato = 1`: senza, il BookingEngine
 * rifiuta la richiesta. Telefono, palazzina e appartamento non sono
 * obbligatori.
 *
 * L'amministratore globale li vede tutti, di ogni condominio, e da qui deve
 * poter leggere a colpo d'occhio a quale appartengono: il condominio non e'
 * un dettaglio anagrafico, e' cio' che ciascuno vede: sale, prenotazioni e
 * palazzine del proprio gruppo e nient'altro. Un amministratore rimasto in
 * Gruppo YEC - com'e' nato ogni utente creato dall'area admin prima che
 * esistesse `assegna_utente` - apre il calendario e non trova nessuna sala.
 */
export default function Utenti() {
    const [userInfo] = useState(() => LoginManager.getUserInfo());
    const globale = isAdminGlobale(userInfo);

    const { items, loading, error, reload } = useEntityList('user', 'surname', 'asc');
    const palazzine = useEntityList('palazzina', 'nome', 'asc');
    // chi ha un condominio solo non ha nulla da distinguere: non li chiede
    const condomini = useEntityList(globale ? 'user_group' : null, 'name', 'asc');

    const [ricerca, setRicerca] = useState('');
    const [filtroStato, setFiltroStato] = useState('tutti');
    const [filtroPalazzina, setFiltroPalazzina] = useState('0');
    const [filtroCondominio, setFiltroCondominio] = useState('0');

    const visibili = useMemo(() => {
        const q = ricerca.trim().toLowerCase();
        return items.filter(u => {
            if (filtroStato === 'abilitati' && parseInt(u.abilitato, 10) !== 1) return false;
            if (filtroStato === 'attesa' && parseInt(u.abilitato, 10) === 1) return false;
            if (filtroPalazzina !== '0' && refId(u.idPalazzina) !== parseInt(filtroPalazzina, 10)) return false;
            if (filtroCondominio !== '0' && refId(u.idUserGroup) !== parseInt(filtroCondominio, 10)) return false;
            if (!q) return true;
            const blob = [u.name, u.surname, u.email, u.telefono].join(' ').toLowerCase();
            return blob.includes(q);
        });
    }, [items, ricerca, filtroStato, filtroPalazzina, filtroCondominio]);

    const [daInviare, setDaInviare] = useState(false);
    const [inviando, setInviando] = useState(false);

    /* A chi partirebbero le credenziali: i condomini abilitati, anche se le
       hanno gia' ricevute. L'amministratore globale deve prima scegliere il
       condominio, gli altri ne hanno uno solo. Stesso criterio del server,
       che resta quello che decide. */
    const idCondominio = globale ? parseInt(filtroCondominio, 10) || 0 : refId(userInfo.idUserGroup);
    const destinatari = useMemo(() => items.filter(u =>
        livelloDi(u) === CONDOMINO
        && parseInt(u.abilitato, 10) === 1
        && Boolean(u.email)
        && (!globale || refId(u.idUserGroup) === idCondominio)
    ), [items, globale, idCondominio]);

    async function inviaATutti() {
        setInviando(true);
        try {
            const res = await UtentiManager.inviaCredenziali({ tutti: true, idUserGroup: idCondominio });
            const esito = (res && res.data) || {};
            if (res && res.success === 1) {
                toast.success(res.message, { theme: 'colored' });
            }
            else {
                toast.error(messaggioErrore(res, 'Invio non riuscito.'), { theme: 'colored' });
            }
            (esito.non_inviate || []).forEach(riga => toast.warn(`Non inviata: ${riga}`, { theme: 'colored' }));
            setDaInviare(false);
            reload();
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setInviando(false);
        }
    }

    const daAbilitare = useMemo(
        () => items.filter(u => parseInt(u.abilitato, 10) !== 1 && richiedeApprovazione(livelloDi(u))).length,
        [items]
    );

    const stato = statoLista({
        loading: loading,
        error: error,
        empty: visibili.length === 0,
        icon: 'users-gear',
        message: items.length === 0
            ? 'Nessun utente registrato.'
            : 'Nessun utente corrisponde ai filtri impostati.',
        onRetry: reload
    });

    return (
        <section className="lv-card lv-card-pad">
            <SectionHead
                eyebrow="Amministrazione utenti"
                title="Anagrafica utenti"
                subtitle={daAbilitare > 0
                    ? `${daAbilitare} ${daAbilitare === 1 ? 'iscritto in attesa' : 'iscritti in attesa'} di approvazione.`
                    : 'Approva le nuove iscrizioni e completa i dati necessari alla prenotazione.'}
                action={
                    <div className="lv-row-actions">
                        <button
                            type="button"
                            className="lv-btn lv-btn-ghost lv-btn-pill"
                            onClick={() => setDaInviare(true)}
                            disabled={globale && !idCondominio}
                            title={globale && !idCondominio ? 'Scegli prima il condominio nel filtro.' : undefined}
                        >
                            <FontAwesomeIcon icon={['fas', 'envelope']} />
                            Invia credenziali
                        </button>
                        <Link to="/admin/utenti/nuovo" className="lv-btn lv-btn-primary lv-btn-pill">
                            <FontAwesomeIcon icon={['fas', 'plus']} />
                            Aggiungi
                        </Link>
                    </div>
                }
            />

            <div className="lv-toolbar">
                <input
                    type="search"
                    className="lv-input"
                    placeholder="Cerca per nome, email o telefono…"
                    value={ricerca}
                    onChange={e => setRicerca(e.target.value)}
                />
                <Select
                    ariaLabel="Stato"
                    value={filtroStato}
                    onChange={setFiltroStato}
                    options={[
                        { value: 'tutti', label: 'Tutti gli stati' },
                        { value: 'abilitati', label: 'Solo abilitati' },
                        { value: 'attesa', label: 'Da abilitare' }
                    ]}
                />
                <Select
                    ariaLabel="Palazzina"
                    value={filtroPalazzina}
                    onChange={setFiltroPalazzina}
                    options={[
                        { value: '0', label: 'Tutte le palazzine' },
                        ...palazzine.items.map(p => ({ value: String(p.id), label: p.nome }))
                    ]}
                />
                {globale && (
                    <Select
                        ariaLabel="Condominio"
                        value={filtroCondominio}
                        onChange={setFiltroCondominio}
                        options={[
                            { value: '0', label: 'Tutti i condomini' },
                            ...condomini.items.map(c => ({ value: String(c.id), label: c.name }))
                        ]}
                    />
                )}
            </div>

            {stato || (
                <div className="lv-table-wrap">
                    <table className="lv-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Contatto</th>
                                {globale && <th>Condominio</th>}
                                <th>Unità</th>
                                <th>Ruolo</th>
                                <th>Stato</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibili.map(u => {
                                const abilitato = parseInt(u.abilitato, 10) === 1;
                                return (
                                    <tr key={u.id}>
                                        <td data-label="Nome">
                                            <div className="lv-cell-strong">{nomeUtente(u)}</div>
                                        </td>
                                        <td data-label="Contatto">
                                            <div className="lv-cell-strong">{u.telefono || '—'}</div>
                                            <div className="lv-cell-sub">{u.email}</div>
                                        </td>
                                        {globale && (
                                            <td data-label="Condominio">
                                                {refLabel(u.idUserGroup, ['name'], 'Da assegnare')}
                                            </td>
                                        )}
                                        <td data-label="Unità">
                                            <div className="lv-cell-strong">{refLabel(u.idPalazzina, ['nome'])}</div>
                                            <div className="lv-cell-sub">
                                                {refLabel(u.idAppartamento, ['identificativo'])}
                                            </div>
                                        </td>
                                        <td data-label="Ruolo">{etichettaLivello(u.level)}</td>
                                        <td data-label="Stato">
                                            <span className={`lv-pill ${abilitato ? 'lv-pill-ok' : 'lv-pill-warn'}`}>
                                                {abilitato ? 'Abilitato' : 'Da abilitare'}
                                            </span>
                                            {livelloDi(u) === CONDOMINO && (
                                                <div className="lv-cell-sub">
                                                    {u.credenziali_inviate_il ? 'Credenziali inviate' : 'Credenziali da inviare'}
                                                </div>
                                            )}
                                        </td>
                                        <td data-label="Azioni">
                                            <div className="lv-row-actions">
                                                <OpenLink to={`/admin/utenti/${u.id}`} />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmModal
                show={daInviare}
                title="Inviare le credenziali?"
                message={destinatari.length === 0
                    ? 'Nessun condomino abilitato con un\'email a cui inviarle.'
                    : `${destinatari.length} ${destinatari.length === 1 ? 'condomino abilitato riceverà' : 'condomini abilitati riceveranno'} per email una password provvisoria nuova, che sostituisce quella attuale, anche se le credenziali le avevano già ricevute. Chi non è abilitato non riceve nulla.`}
                confirmLabel="Invia"
                working={inviando}
                onConfirm={destinatari.length === 0 ? () => setDaInviare(false) : inviaATutti}
                onClose={() => setDaInviare(false)}
            />
        </section>
    );
}
